/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { ActivityLogAlerts } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { MonitorClient } from "../monitorClient";
import {
  ActivityLogAlertResource,
  ActivityLogAlertsListBySubscriptionIdOptionalParams,
  ActivityLogAlertsListByResourceGroupOptionalParams,
  ActivityLogAlertsCreateOrUpdateOptionalParams,
  ActivityLogAlertsCreateOrUpdateResponse,
  ActivityLogAlertsGetOptionalParams,
  ActivityLogAlertsGetResponse,
  ActivityLogAlertsDeleteOptionalParams,
  ActivityLogAlertPatchBody,
  ActivityLogAlertsUpdateOptionalParams,
  ActivityLogAlertsUpdateResponse,
  ActivityLogAlertsListBySubscriptionIdResponse,
  ActivityLogAlertsListByResourceGroupResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing ActivityLogAlerts operations. */
export class ActivityLogAlertsImpl implements ActivityLogAlerts {
  private readonly client: MonitorClient;

  /**
   * Initialize a new instance of the class ActivityLogAlerts class.
   * @param client Reference to the service client
   */
  constructor(client: MonitorClient) {
    this.client = client;
  }

  /**
   * Get a list of all activity log alerts in a subscription.
   * @param options The options parameters.
   */
  public listBySubscriptionId(
    options?: ActivityLogAlertsListBySubscriptionIdOptionalParams
  ): PagedAsyncIterableIterator<ActivityLogAlertResource> {
    const iter = this.listBySubscriptionIdPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listBySubscriptionIdPagingPage(options);
      }
    };
  }

  private async *listBySubscriptionIdPagingPage(
    options?: ActivityLogAlertsListBySubscriptionIdOptionalParams
  ): AsyncIterableIterator<ActivityLogAlertResource[]> {
    let result = await this._listBySubscriptionId(options);
    yield result.value || [];
  }

  private async *listBySubscriptionIdPagingAll(
    options?: ActivityLogAlertsListBySubscriptionIdOptionalParams
  ): AsyncIterableIterator<ActivityLogAlertResource> {
    for await (const page of this.listBySubscriptionIdPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Get a list of all activity log alerts in a resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  public listByResourceGroup(
    resourceGroupName: string,
    options?: ActivityLogAlertsListByResourceGroupOptionalParams
  ): PagedAsyncIterableIterator<ActivityLogAlertResource> {
    const iter = this.listByResourceGroupPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listByResourceGroupPagingPage(resourceGroupName, options);
      }
    };
  }

  private async *listByResourceGroupPagingPage(
    resourceGroupName: string,
    options?: ActivityLogAlertsListByResourceGroupOptionalParams
  ): AsyncIterableIterator<ActivityLogAlertResource[]> {
    let result = await this._listByResourceGroup(resourceGroupName, options);
    yield result.value || [];
  }

  private async *listByResourceGroupPagingAll(
    resourceGroupName: string,
    options?: ActivityLogAlertsListByResourceGroupOptionalParams
  ): AsyncIterableIterator<ActivityLogAlertResource> {
    for await (const page of this.listByResourceGroupPagingPage(
      resourceGroupName,
      options
    )) {
      yield* page;
    }
  }

  /**
   * Create a new activity log alert or update an existing one.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param activityLogAlertName The name of the activity log alert.
   * @param activityLogAlert The activity log alert to create or use for the update.
   * @param options The options parameters.
   */
  createOrUpdate(
    resourceGroupName: string,
    activityLogAlertName: string,
    activityLogAlert: ActivityLogAlertResource,
    options?: ActivityLogAlertsCreateOrUpdateOptionalParams
  ): Promise<ActivityLogAlertsCreateOrUpdateResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, activityLogAlertName, activityLogAlert, options },
      createOrUpdateOperationSpec
    );
  }

  /**
   * Get an activity log alert.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param activityLogAlertName The name of the activity log alert.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    activityLogAlertName: string,
    options?: ActivityLogAlertsGetOptionalParams
  ): Promise<ActivityLogAlertsGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, activityLogAlertName, options },
      getOperationSpec
    );
  }

  /**
   * Delete an activity log alert.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param activityLogAlertName The name of the activity log alert.
   * @param options The options parameters.
   */
  delete(
    resourceGroupName: string,
    activityLogAlertName: string,
    options?: ActivityLogAlertsDeleteOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { resourceGroupName, activityLogAlertName, options },
      deleteOperationSpec
    );
  }

  /**
   * Updates an existing ActivityLogAlertResource's tags. To update other fields use the CreateOrUpdate
   * method.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param activityLogAlertName The name of the activity log alert.
   * @param activityLogAlertPatch Parameters supplied to the operation.
   * @param options The options parameters.
   */
  update(
    resourceGroupName: string,
    activityLogAlertName: string,
    activityLogAlertPatch: ActivityLogAlertPatchBody,
    options?: ActivityLogAlertsUpdateOptionalParams
  ): Promise<ActivityLogAlertsUpdateResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        activityLogAlertName,
        activityLogAlertPatch,
        options
      },
      updateOperationSpec
    );
  }

  /**
   * Get a list of all activity log alerts in a subscription.
   * @param options The options parameters.
   */
  private _listBySubscriptionId(
    options?: ActivityLogAlertsListBySubscriptionIdOptionalParams
  ): Promise<ActivityLogAlertsListBySubscriptionIdResponse> {
    return this.client.sendOperationRequest(
      { options },
      listBySubscriptionIdOperationSpec
    );
  }

  /**
   * Get a list of all activity log alerts in a resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  private _listByResourceGroup(
    resourceGroupName: string,
    options?: ActivityLogAlertsListByResourceGroupOptionalParams
  ): Promise<ActivityLogAlertsListByResourceGroupResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, options },
      listByResourceGroupOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/microsoft.insights/activityLogAlerts/{activityLogAlertName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.ActivityLogAlertResource
    },
    201: {
      bodyMapper: Mappers.ActivityLogAlertResource
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  requestBody: Parameters.activityLogAlert,
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.activityLogAlertName
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer
};
const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/microsoft.insights/activityLogAlerts/{activityLogAlertName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ActivityLogAlertResource
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.activityLogAlertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/microsoft.insights/activityLogAlerts/{activityLogAlertName}",
  httpMethod: "DELETE",
  responses: {
    200: {},
    204: {},
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.activityLogAlertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const updateOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/microsoft.insights/activityLogAlerts/{activityLogAlertName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.ActivityLogAlertResource
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  requestBody: Parameters.activityLogAlertPatch,
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.activityLogAlertName
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer
};
const listBySubscriptionIdOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/microsoft.insights/activityLogAlerts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ActivityLogAlertList
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer
};
const listByResourceGroupOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/microsoft.insights/activityLogAlerts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ActivityLogAlertList
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId
  ],
  headerParameters: [Parameters.accept],
  serializer
};
